import { rankCandidates } from "@stockline/product-search";
import type { ProductResponse } from "@/app/api/types/product";
import type { SearchResult, SearchOptions } from "./types";
import { productSearchIndex } from "./searchIndex";

/**
 * Heuristic: short, mostly-alphanumeric, dash/underscore tokens look like SKUs.
 * Used by some UI paths to bias keyboard handling — not part of the ranker.
 */
export function likelyIsSku(searchTerm: string): boolean {
  if (!searchTerm) return false;
  const trimmed = searchTerm.trim();

  if (trimmed.length <= 4) return true;
  if (/^\d/.test(trimmed)) return true;
  if (/^[A-Z]{2,4}\d/.test(trimmed.toUpperCase())) return true;

  const hasSpaces = trimmed.includes(" ");
  const alphanumericRatio =
    (trimmed.match(/[a-zA-Z0-9-_]/g)?.length || 0) / trimmed.length;
  if (!hasSpaces && alphanumericRatio > 0.9 && trimmed.length <= 12)
    return true;

  return false;
}

/**
 * Frontend-facing product search. Thin adapter over `@stockline/product-search`
 * — the same engine that powers the V2 API search tool the AI agent uses, so
 * a query produces the same ranking in both places.
 *
 * Frontend defaults differ from the package defaults: `includeNonMatching`
 * is `true` here so the infinite-scroll list always has every product.
 */
export function searchProductsEnhanced(
  products: ProductResponse[],
  query: string,
  options: SearchOptions = {},
): SearchResult<ProductResponse>[] {
  // Empty query: surface most-selected products first via the frequency
  // index. The shared ranker is intentionally frequency-agnostic (V2 API
  // doesn't track selection frequency); ordering by use is a frontend UX
  // concern that we keep wired here.
  if (!query || query.trim() === "") {
    const orderedIds = productSearchIndex.getOrCreateIndex("", products);
    const productMap = new Map(products.map((p) => [p.id as string, p]));
    return orderedIds
      .map((id) => productMap.get(id))
      .filter((p): p is ProductResponse => p !== undefined)
      .map((item) => ({
        item,
        score: 0,
        matchType: "none" as const,
      }));
  }

  const results = rankCandidates<ProductResponse>(products, query, {
    includeNonMatching: true,
    normalizeNumbers: true,
    boostExactOrder: true,
    ...options,
  });

  // Cache the result order for paginated rendering.
  productSearchIndex.getOrCreateIndex(
    query,
    results.map((r) => r.item),
    () => results.map((r) => r.item),
  );

  return results;
}

/** Get product search results as an ID index (for pagination). */
export function getProductSearchIndex(
  products: ProductResponse[],
  query: string,
): string[] {
  return searchProductsEnhanced(products, query).map((r) => r.item.id);
}

/** Track product selection so the default (empty-query) ordering surfaces frequent picks. */
export function trackProductSelection(productId: string): void {
  productSearchIndex.trackSelection(productId);
}
